/*
  I suspect that image size breakpoints probably make sense to be
  used with max-width rather than min-width;
  https://www.dofactory.com/html/img/sizes
  https://medium.com/@MRWwebDesign/responsive-images-the-sizes-attribute-and-unexpected-image-sizes-882a2eadb6db
  https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images#resolution_switching_different_sizes
*/

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

type ValueType = 'px' | 'vw' | 'rem' | 'em';

const breakpoints: Breakpoints = {
  sm: 640,
  // => @media (min-width: 640px) { ... }

  md: 768,
  // => @media (min-width: 768px) { ... }

  lg: 1024,
  // => @media (min-width: 1024px) { ... }

  xl: 1280,
  // => @media (min-width: 1280px) { ... }

  '2xl': 1536,
  // => @media (min-width: 1536px) { ... }
};

type Breakpoints = {
  [key in Breakpoint]: number;
};

type TargetSize = `${number}${ValueType}` | number | `calc(${string})`;

type TargetSizes = {
  // eslint-disable-next-line no-unused-vars
  [Property in Breakpoint]?: TargetSize;
};

export function tailwindBreakpointSizesToImageSizes(
  targetSizes: TargetSizes,
  fallbackBaseSize: TargetSize = '100vw',
) {
  // order important here
  const breakpointsInOrder: Breakpoint[] = ['2xl', 'xl', 'md', 'lg', 'sm'];

  const result = breakpointsInOrder
    .map((breakpoint: Breakpoint) => {
      if (!targetSizes.hasOwnProperty(breakpoint)) {
        return null;
      }

      const value = targetSizes[breakpoint];

      if (!value) {
        return null;
      }

      return breakpointToMediaQuery(breakpoint, value);
    })
    .reduce((prev, current) => {
      if (typeof current === 'string') {
        return [...prev, current];
      }

      return prev;
    }, [] as string[]);

  return [...result, targetSizeConverter(fallbackBaseSize)].join(', ');
}

function breakpointToMediaQuery(
  breakpoint: Breakpoint,
  targetSize: TargetSize | number,
) {
  return `(min-width: ${breakpoints[breakpoint]}px) ${targetSizeConverter(
    targetSize,
  )}`;
}

function targetSizeConverter(targetSize: TargetSize) {
  return typeof targetSize === 'number' ? `${targetSize}px` : targetSize;
}
